import { useState } from "react";
import SectionHeading from "@/components/base/SectionHeading";
import { debates } from "@/mocks/spotlight";

export default function OpinionArena() {
  const [activeId, setActiveId] = useState(debates[0].id);
  const [likeState, setLikeState] = useState<Record<string, { count: number; liked: boolean }>>({});

  const debate = debates.find((item) => item.id === activeId) ?? debates[0];

  const getLike = (id: string, base: number) => likeState[id] ?? { count: base, liked: false };

  const toggleLike = (id: string, base: number) => {
    setLikeState((prev) => {
      const current = prev[id] ?? { count: base, liked: false };
      return {
        ...prev,
        [id]: {
          count: current.liked ? current.count - 1 : current.count + 1,
          liked: !current.liked,
        },
      };
    });
  };

  return (
    <section id="arena" className="w-full bg-background-50">
      <div className="mx-auto w-full max-w-[1200px] px-4 md:px-6 py-14 md:py-20">
        <SectionHeading
          eyebrow="Opinion Arena"
          title="观点擂台"
          desc="同一个话题，正反两种立场并排呈现。先看双方的核心论据，再决定你更认同哪一边。"
        />

        <div className="flex flex-wrap items-center gap-2">
          {debates.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => setActiveId(item.id)}
              className={`cursor-pointer whitespace-nowrap rounded-full border px-4 py-2 font-label text-sm transition-colors ${
                activeId === item.id
                  ? "border-primary-500 bg-primary-500 text-background-50"
                  : "border-background-300 text-foreground-700 hover:border-primary-300 hover:text-primary-600"
              }`}
            >
              {item.label}
            </button>
          ))}
        </div>

        <div className="mt-8 rounded-lg border border-background-200 bg-background-100 p-5 md:p-8">
          <h3 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
            {debate.question}
          </h3>

          <div className="mt-6 grid grid-cols-1 gap-5 md:grid-cols-2">
            <div className="rounded-lg border border-primary-200 bg-primary-50/70 p-5">
              <div className="flex items-center gap-2">
                <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary-500 text-background-50">
                  <i className="ri-thumb-up-line"></i>
                </span>
                <h4 className="font-heading text-base font-semibold text-primary-800">
                  正方 · {debate.pro.title}
                </h4>
              </div>
              <ul className="mt-4 space-y-3">
                {debate.pro.points.map((point) => (
                  <li key={point} className="flex gap-2.5 text-sm leading-relaxed text-foreground-700">
                    <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center text-primary-600">
                      <i className="ri-check-line"></i>
                    </span>
                    {point}
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-lg border border-secondary-200 bg-secondary-50/80 p-5">
              <div className="flex items-center gap-2">
                <span className="flex h-8 w-8 items-center justify-center rounded-md bg-secondary-500 text-background-50">
                  <i className="ri-thumb-down-line"></i>
                </span>
                <h4 className="font-heading text-base font-semibold text-secondary-900">
                  反方 · {debate.con.title}
                </h4>
              </div>
              <ul className="mt-4 space-y-3">
                {debate.con.points.map((point) => (
                  <li key={point} className="flex gap-2.5 text-sm leading-relaxed text-foreground-700">
                    <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center text-secondary-600">
                      <i className="ri-close-line"></i>
                    </span>
                    {point}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="mt-8">
            <h4 className="flex items-center gap-2 font-heading text-base font-semibold text-foreground-950">
              <span className="flex h-5 w-5 items-center justify-center text-primary-600">
                <i className="ri-chat-smile-2-line"></i>
              </span>
              争议热评精选
            </h4>
            <div className="mt-4 space-y-3">
              {debate.comments.map((comment) => {
                const like = getLike(comment.id, comment.likes);
                return (
                  <div
                    key={comment.id}
                    className="rounded-lg border border-background-200 bg-background-50 p-4"
                  >
                    <div className="flex items-start gap-3">
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-foreground-100 font-heading text-sm font-semibold text-foreground-700">
                        {comment.user.slice(0, 1)}
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-label text-sm font-medium text-foreground-900">
                            {comment.user}
                          </span>
                          <span
                            className={`rounded-full px-2 py-0.5 font-label text-xs ${
                              comment.stance === "pro"
                                ? "bg-primary-100 text-primary-700"
                                : "bg-secondary-100 text-secondary-900"
                            }`}
                          >
                            {comment.stance === "pro" ? "正方观点" : "反方观点"}
                          </span>
                        </div>
                        <p className="mt-2 text-sm leading-relaxed text-foreground-700">{comment.text}</p>
                        <button
                          type="button"
                          onClick={() => toggleLike(comment.id, comment.likes)}
                          className={`mt-3 inline-flex cursor-pointer items-center gap-1.5 rounded-full border px-3 py-1 font-label text-xs transition-colors ${
                            like.liked
                              ? "border-primary-300 bg-primary-100 text-primary-700"
                              : "border-background-300 text-foreground-500 hover:border-primary-300 hover:text-primary-600"
                          }`}
                        >
                          <i className={like.liked ? "ri-heart-fill" : "ri-heart-line"}></i>
                          {like.count}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}